import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { IconButton, InputAdornment, Stack } from '@mui/material';
import type { Control } from 'react-hook-form';
import { ControlledTextField } from '../../../../inputs/forms/controlled-text-field/controlled-text-field';
import type { LoginFormData } from '../login-page.types';
import { Button } from '../../../../primitives/button';
import { RevealedFieldError } from '../revealed-field-error/revealed-field-error';

type LoginCredentialsSectionProps = {
  control: Control<LoginFormData>;
  isSubmitting: boolean;
  isDisabled?: boolean;
  emailError: boolean;
  passwordError: boolean;
  showPassword: boolean;
  onTogglePassword: () => void;
};

export const LoginCredentialsSection = ({
  control,
  isSubmitting,
  isDisabled,
  emailError,
  passwordError,
  showPassword,
  onTogglePassword,
}: LoginCredentialsSectionProps) => {
  const passwordAdornment = (
    <InputAdornment position="end">
      <IconButton
        aria-label={showPassword ? 'Hide password' : 'Show password'}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onTogglePassword}
        edge="end"
        size="large" // §VI — size="large" clears Trackd's 44px touch-target floor
      >
        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <>
      <Stack sx={{ width: '100%', gap: 1.5 }}>
        <ControlledTextField
          name="email"
          control={control}
          disabled={isSubmitting || isDisabled}
          label="Email"
          className="MuiFormControl-noMargin"
          slots={{ formHelperText: RevealedFieldError }}
          fullWidth
          error={emailError}
          type="email"
          autoComplete="username"
        />

        <ControlledTextField
          name="password"
          autoComplete="current-password"
          control={control}
          disabled={isSubmitting || isDisabled}
          label="Password"
          className="MuiFormControl-noMargin"
          slots={{ formHelperText: RevealedFieldError }}
          fullWidth
          error={passwordError}
          type={showPassword ? 'text' : 'password'}
          slotProps={{ input: { endAdornment: passwordAdornment } }}
        />
      </Stack>

      <Button
        type="submit"
        size="large"
        disabled={isSubmitting || isDisabled}
        loadingPosition="start"
        loading={isSubmitting}
        fullWidth
      >
        Sign in
      </Button>

      {/* Overlaps the column gap so the link sits close without shrinking its touch target. */}
      <Button to="/forgot-password" sx={{ my: -1 }}>
        Forgot your password?
      </Button>
    </>
  );
};
